import { useNavigate } from "react-router-dom";
import {
  Settings, User, Building2, Shield, CreditCard, Bell, Palette, Link2,
  Lock, ScrollText, Award, FileText, ChevronRight, UserCog, HelpCircle
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../context/AuthContext";

const SettingsHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const base = user?.role === "super_admin" ? "/admin" : "/tenant";

  const categories = [
    {
      title: "Your Preferences",
      items: [
        { icon: User, label: "Profile & Preferences", desc: "Manage your personal info, timezone, and language", path: `${base}/settings/profile`, color: "text-blue-600", bg: "bg-blue-50" },
        { icon: Bell, label: "Notifications", desc: "Email alerts, digest frequency, and quiet hours", path: `${base}/settings/notifications`, color: "text-amber-600", bg: "bg-amber-50" },
        { icon: Lock, label: "Security", desc: "Password, 2FA, session timeout, and login activity", path: `${base}/settings/security`, color: "text-red-600", bg: "bg-red-50" },
      ],
    },
    {
      title: "Account Setup",
      items: [
        { icon: Building2, label: "Account & Organization", desc: "Company details, industry, and timezone settings", path: `${base}/settings/account`, color: "text-indigo-600", bg: "bg-indigo-50" },
        { icon: Palette, label: "Branding", desc: "Logo, color scheme, and certificate appearance", path: `${base}/settings/branding`, color: "text-violet-600", bg: "bg-violet-50" },
        { icon: Building2, label: "Departments", desc: "Manage departments and organizational structure", path: `${base}/settings/departments`, color: "text-teal-600", bg: "bg-teal-50" },
      ],
    },
    {
      title: "Data & Integrations",
      items: [
        { icon: Link2, label: "Integrations", desc: "M365, Google Workspace, SCIM, and SMTP setup", path: `${base}/settings/integrations`, color: "text-emerald-600", bg: "bg-emerald-50" },
        { icon: ScrollText, label: "Audit Logs", desc: "Track changes and admin activity across the platform", path: `${base}/settings/audit`, color: "text-slate-600", bg: "bg-slate-100" },
        { icon: UserCog, label: "Roles & Permissions", desc: "Configure RBAC and custom role access", path: `${base}/settings/rbac`, color: "text-orange-600", bg: "bg-orange-50" },
      ],
    },
    {
      title: "Billing & Plans",
      items: [
        { icon: CreditCard, label: "Billing & Invoices", desc: "Plan details, payment methods, and invoice history", path: `${base}/billing`, color: "text-green-600", bg: "bg-green-50" },
        { icon: FileText, label: "Subscription", desc: "View and upgrade your current plan", path: `${base}/settings/subscription`, color: "text-primary", bg: "bg-primary/5" },
      ],
    },
    {
      title: "Platform",
      items: [
        { icon: Award, label: "Certificates", desc: "Certificate generation, logos, and verification settings", path: `${base}/settings/certificates`, color: "text-yellow-600", bg: "bg-yellow-50" },
        { icon: Shield, label: "Compliance Settings", desc: "Compliance deadlines, reminders, and auto-assignment rules", path: `${base}/settings/notifications`, color: "text-red-600", bg: "bg-red-50", badge: "Reminders" },
        { icon: HelpCircle, label: "Support", desc: "Submit a ticket, track issues, and browse help", path: `${base}/support`, color: "text-blue-600", bg: "bg-blue-50" },
      ],
    },
  ];

  return (
    <AdminLayout title="Settings" variant={variant}>
      <div className="space-y-6 max-w-5xl" data-testid="settings-hub">
        <div>
          <h2 className="text-lg font-bold tracking-tight">Settings</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your account, organization, and platform preferences</p>
        </div>

        {categories.map((cat, ci) => (
          <div key={ci}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{cat.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {cat.items.map((item, ii) => (
                <Card
                  key={ii}
                  className="border shadow-none cursor-pointer transition-all hover:shadow-sm hover:border-primary/30 group"
                  onClick={() => navigate(item.path)}
                  data-testid={`settings-card-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center shrink-0`}>
                      <item.icon className={`w-5 h-5 ${item.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold group-hover:text-primary transition-colors">{item.label}</h4>
                        {item.badge && <Badge variant="outline" className="text-[9px] px-1.5 py-0">{item.badge}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary/50 transition-colors mt-0.5 shrink-0" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default SettingsHub;
